import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { selectClipsInOrder, selectStudioVideos } from '../studio.selector';
import { Store } from '@ngrx/store';
import { StudioVideoShapeDescriptor } from '../studio.types';

export interface StudioMedia {
  uploadedAt?: string;
  id: string;
  s3Id?: string;
  url?: string;
  fileName?: string;
  extension?: string;
  sizeInMb?: number;
  width?: number;
  height?: number;
}

export interface StudioVideo extends StudioMedia {
  id: string;
  duration?: number;
  tmpThumbnailUrl?: string;
  positionIndex?: number;
  instances?: Record<string, any>; // VideoInstance
  cell?: any; // StudioCell
}

type QueueItem = {
  url: string;
  from: number;
  to: number;
};

type Clip = { from: number; to: number };

type VideoProvider = (id: string) => StudioVideo | undefined;

type VideoFrameCallbackHandle = number;
type RVFC = (
  cb: (now: number, metadata: any) => void,
) => VideoFrameCallbackHandle;

@Injectable({ providedIn: 'root' })
export class VideoPlayerService {
  private videoA!: HTMLVideoElement;
  private videoB!: HTMLVideoElement;

  private active!: HTMLVideoElement;
  private standby!: HTMLVideoElement;

  private queue: Array<{ url: string; from: number; to: number }> = [];

  private stopped = false;

  /**
   * Provide a resolver so the service can turn a videoId into a URL.
   * (You can wire this from your store/DB layer.)
   */
  public setVideoProvider(provider: VideoProvider) {
    this.videoProvider = provider;
  }

  /**
   * The VideoShape (Pixi) can subscribe and always render the active video element.
   * Example: PIXI.Texture.from(videoEl)
   */
  public readonly activeVideo$ = new BehaviorSubject<HTMLVideoElement | null>(
    null,
  );

  private videoProvider: VideoProvider | null = null;

  // Two-element ping-pong

  // Prepared clips per videoId
  private prepared = new Map<string, { cell: any; clips: Clip[] }>();

  // Playback state
  private playingVideoId: string | null = null;
  private playingClipIndex = 0;
  private playingClips: Clip[] = [];
  private isPlaying = false;
  private cancelToken = 0;

  // Frame timing
  private rafId: number | null = null;
  private rvfcHandle: VideoFrameCallbackHandle | null = null;

  // Store the clips in order from the selector
  clipsInOrder: StudioVideoShapeDescriptor[] = [];

  constructor(
    private zone: NgZone,
    private readonly store: Store,
  ) {
    this.store.select(selectStudioVideos).subscribe(videos => {
      this.videos = videos;
    });

    this.store.select(selectClipsInOrder).subscribe(clips => {
      // console.log('setting-clips-in-order', clips);
      this.clipsInOrder = clips;
    });
  }

  /**
   * Initialize the player using the clips from the store (selectClipsInOrder)
   */
  async initializePlayerFromStore(): Promise<{
    videoA: HTMLVideoElement;
    videoB: HTMLVideoElement;
  }> {
    return this.initializePlayer(this.clipsInOrder);
  }

  /**
   * Prepare a shape/video for playback (store clip list, optionally warm up metadata).
   * Assumption: descriptor.id is the StudioVideo.id you will play.
   */

  // ---------------------------
  // Internal helpers
  // ---------------------------

  videos: Record<string, StudioVideo>;
  async initializePlayer(videos: StudioVideoShapeDescriptor[]): Promise<{
    videoA: HTMLVideoElement;
    videoB: HTMLVideoElement;
  }> {
    // 1️⃣ Flatten videos + clips into a queue
    const queue: QueueItem[] = [];
    console.log('videos', this.videos);
    for (const videoShape of videos) {
      const video = this.videos[videoShape.videoId];
      for (const clip of videoShape.clips) {
        queue.push({
          url: this.getUrl(video),
          from: clip.from,
          to: clip.to,
        });
      }
    }

    this.queue = queue;
    // -- // console.log('initializePlayer', queue); // -- //

    if (queue.length === 0) {
      throw new Error('No clips to play');
    }

    // 2️⃣ Create two video elements (pooled)
    const videoA = document.createElement('video');
    const videoB = document.createElement('video');

    [videoA, videoB].forEach(v => {
      v.preload = 'metadata';
      // v.preload = 'auto';
      v.crossOrigin = 'anonymous';
      v.playsInline = true;
      v.muted = true;
      v.autoplay = false;
      v.loop = false;
      v.controls = false;
    });

    // 3️⃣ Prime first queue item into videoA
    await this.prime(videoA, queue[0]);

    // 4️⃣ Prime second queue item into videoB (if exists)
    if (queue.length > 1) {
      await this.prime(videoB, queue[1]);
    }

    this.videoA = videoA;
    this.videoB = videoB;

    this.activeVideo$.next(videoA);

    this.active = videoA;
    this.standby = videoB;

    return { videoA, videoB };
  }

  index = 0;
  async play() {
    console.log('play', this.queue);
    if (!this.queue.length) return;

    console.log('play', this.active);
    await this.active.play();

    while (!this.stopped && this.index < this.queue.length) {
      const current = this.queue[this.index];
      const next = this.queue[this.index + 1];

      // Pre-prime next (if exists)
      if (next) {
        await this.prime(this.standby, next);
      }

      // Wait until clip end
      console.log('waitUntilClipEnd', current.to);
      await this.waitUntilClipEnd(this.active, current.to);
      console.log('waitUntilClipEnd > done', next);

      if (!next) {
        this.active.pause();
        return;
      }

      // Swap
      this.active.pause();

      const tmp = this.active;
      this.active = this.standby;
      this.standby = tmp;
      this.activeVideo$.next(this.active);

      this.index++;

      await this.active.play();
    }
  }

  private async waitUntilClipEnd(video: HTMLVideoElement, to: number) {
    const guard = 0.02;
    console.log('waitUntilClipEnd', to);
    await new Promise<void>(resolve => {
      const tick = () => {
        if (video.currentTime >= to - guard) {
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  getUrl(video: StudioVideo) {
    return `https://private.cdn.inwedio.com/videos/${video.id}/video.${video.extension}`;
  }

  stop() {
    this.stopped = true;
    this.videoA?.pause();
    this.videoB?.pause();
  }

  async prime(video: HTMLVideoElement, item: QueueItem): Promise<void> {
    // console.log('prime', item);
    video.src = item.url;
    video.load();

    await this.waitForEvent(video, 'loadedmetadata');

    video.currentTime = item.from;
    // Wait for seek to complete
    await this.waitForEvent(video, 'seeked');
  }

  waitForEvent(el: HTMLMediaElement, event: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const onSuccess = () => {
        cleanup();
        resolve();
      };

      const onError = () => {
        cleanup();
        reject(new Error('Media error'));
      };

      const cleanup = () => {
        el.removeEventListener(event, onSuccess);
        el.removeEventListener('error', onError);
      };

      el.addEventListener(event, onSuccess, { once: true });
      el.addEventListener('error', onError, { once: true });
    });
  }

  private createVideoEl(): HTMLVideoElement {
    const el = document.createElement('video');
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';
    el.playsInline = true;
    el.muted = true; // keep autoplay policies happy; unmute if user gesture
    el.loop = false;
    el.controls = false;
    el.disablePictureInPicture = true;
    // iOS/Safari sometimes behaves better with these:
    // (el as any).webkitPlaysInline = true;
    return el;
  }

  private getVideoOrThrow(id: string): StudioVideo {
    if (!this.videoProvider)
      throw new Error('VideoPlayerService: videoProvider is not set');
    const v = this.videoProvider(id);
    if (!v) throw new Error(`VideoPlayerService: unknown video id: ${id}`);
    return v;
  }
}
